// "Give items" (spec/dashboard.md section 11.2): the inventory, the item (the release branch's `SmartObjects.Item`
// documents by label) and the amount; the result is one staged edit of the `inventories` key (model.ts `giveItem`).
import { useMemo, useState } from 'react';
import { ops } from '../api/operations';
import type { Document, Json, Page } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { useContent } from '../content/ContentContext';
import type { DocumentLike } from '../content/format';
import { useGame } from '../shell/GameContext';
import { Button } from '../ui/Button';
import { Combobox, type ComboboxOption } from '../ui/Combobox';
import { Field } from '../ui/Field';
import { NumberInput, Select } from '../ui/inputs';
import { Modal } from '../ui/Modal';
import { useQuery } from '../workspace/cache';
import { INVENTORIES, ITEM_TEMPLATE, giveItem, type InventoryName } from './model';
import type { ReleaseInfo } from './releaseCatalog';

/** The items of the release's source branch (heirs of `SmartObjects.Item` included), labelled through the catalog. */
export function useReleaseItems(release: ReleaseInfo): { options: ComboboxOption[]; loading: boolean; error: unknown } {
  const { client } = useAuth();
  const { org, game } = useGame();
  const catalog = useContent();
  const branch = release.branch;
  const q = useQuery(branch ? `game:${org}/${game.slug}:items:${branch}` : null, () =>
    client.all<Document>((cursor) => client.data(ops.listDocuments, { path: { org, game: game.slug, branch: branch! }, query: { template: ITEM_TEMPLATE, includeHeirs: 'true', limit: 200, cursor: cursor ?? undefined } }) as Promise<Page<Document>>),
  );
  const options = useMemo(
    () =>
      (q.data ?? [])
        .map((d) => ({ value: d.id, label: catalog.label(d as unknown as DocumentLike), hint: d.unnyId ?? d.id.slice(0, 8) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [q.data, catalog],
  );
  return { options, loading: q.loading, error: q.error };
}

export function GiveItemsDialog({ open, release, inventories, onClose, onStage }: { open: boolean; release: ReleaseInfo; inventories: Json; onClose: () => void; onStage: (path: string, value: Json) => void }) {
  const [inventory, setInventory] = useState<InventoryName>('items');
  const [item, setItem] = useState<string | null>(null);
  const [amount, setAmount] = useState(1);
  const items = useReleaseItems(release);
  const ok = !!item && Number.isInteger(amount) && amount > 0;
  const give = () => {
    if (!item) return;
    const r = giveItem(inventories, inventory, item, amount, Math.floor(Date.now() / 1000));
    onStage(r.path, r.value);
  };
  return (
    <Modal
      title="Give items"
      open={open}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!ok} onClick={give}>
            Give
          </Button>
        </>
      }
    >
      <p className="muted small">The grant is staged on the player's inventory; Save All writes it with your reason, and the history makes it undoable.</p>
      <Field label="Inventory">
        <Select value={inventory} onChange={(e) => setInventory(e.target.value as InventoryName)} aria-label="Inventory">
          {INVENTORIES.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Item" required help={!release.branch ? 'No pinned release: the items cannot be listed.' : undefined}>
        <Combobox value={item} options={items.options} onChange={setItem} loading={items.loading} placeholder="Pick an item" aria-label="Item" disabled={!release.branch} />
      </Field>
      <Field label="Amount" required>
        <NumberInput kind="int" value={amount} onCommit={(v) => setAmount(Number(v))} min={1} aria-label="Amount" />
      </Field>
    </Modal>
  );
}
